/**
 * Holds the browser's install dialog until someone asks for it.
 *
 * Chrome and Edge fire `beforeinstallprompt` once, early, and only a listener
 * that already exists catches it. `main.tsx` attaches one listener for the
 * whole app before the first render, so the event is still in hand when a
 * person later reaches the install page or the home-screen card.
 */

export interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

export interface InstallPromptState {
  event: BeforeInstallPromptEvent | null
  /** The browser reported the app installed (`appinstalled`, or an accepted prompt). */
  isInstalled: boolean
}

export type PromptOutcome = 'accepted' | 'dismissed' | 'unavailable'

let state: InstallPromptState = { event: null, isInstalled: false }
const listeners = new Set<() => void>()

function set(next: InstallPromptState) {
  state = next
  for (const listener of listeners) listener()
}

export function subscribeInstallPrompt(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function getInstallPromptState(): InstallPromptState {
  return state
}

/** Start listening. Returns the cleanup. */
export function attachInstallPromptListeners(target: Window = window): () => void {
  const onPrompt = (e: Event) => {
    // Hold it for our own button instead of letting the browser show its mini bar.
    e.preventDefault()
    set({ ...state, event: e as BeforeInstallPromptEvent })
  }
  const onInstalled = () => set({ event: null, isInstalled: true })
  target.addEventListener('beforeinstallprompt', onPrompt)
  target.addEventListener('appinstalled', onInstalled)
  return () => {
    target.removeEventListener('beforeinstallprompt', onPrompt)
    target.removeEventListener('appinstalled', onInstalled)
  }
}

/** Open the browser's install dialog. A captured event works once, so it is dropped after use. */
export async function promptInstall(): Promise<PromptOutcome> {
  const event = state.event
  if (!event) return 'unavailable'
  try {
    await event.prompt()
    const { outcome } = await event.userChoice
    set({ event: null, isInstalled: outcome === 'accepted' || state.isInstalled })
    return outcome
  } catch {
    set({ ...state, event: null })
    return 'unavailable'
  }
}

/** Test seam: back to a clean slate. */
export function resetInstallPromptStore(): void {
  state = { event: null, isInstalled: false }
  listeners.clear()
}
